import _ from 'lodash'
import { forwardRef } from 'react'
import classnames from 'classnames'
import warning from 'warning'
import cxs from 'cxs'

import getColor from '~/lib/getColor'
import getGenericDataId from '~/lib/getGenericDataId'

import {
    asCsx,
    getGridPrefix,
    getFlexboxGridClass,
    asPrefixString,
    gridClassMap,
} from './logic'
import { KEY_OF_GRID_CLASS_MAP_TYPE, BoxPropsType } from './type'
import styles from './styles.css'

export default forwardRef(function Box(props: BoxPropsType, ref) {
    const {
        align,
        textAlign,
        alignItems,
        alignSelf,
        alignContent,
        children,
        className,
        cursor,
        display = 'flex',
        direction,
        element: Elem = 'div',
        flex,
        flexGrow,
        flexShrink,
        flexBasis,
        justify,
        justifyContent,
        eventKey,
        flexWrap,
        height,
        hidden = false,
        width,
        maxHeight,
        maxWidth,
        minHeight,
        minWidth,
        margin,
        marginBottom,
        marginLeft,
        marginRight,
        marginTop,
        padding,
        paddingBottom,
        paddingLeft,
        paddingRight,
        paddingTop,
        borderWidth,
        borderStyle,
        borderColor,
        borderRadius,
        border,
        borderBottom,
        borderLeft,
        borderRight,
        borderTop,
        position,
        color,
        fontSize,
        background,
        backgroundColor,
        hover,
        textOverflow,
        whiteSpace,
        borderCollapse,
        overflow,
        overflowX,
        overflowY,
        ...newProps
    } = props

    const _justifyContent = justifyContent || justify
    const _alignItems = alignItems || align

    warning(
        align == null,
        '[Deprecated] `align` is no longer supported please use alignItems',
    )
    warning(
        justify == null,
        '[Deprecated] `justify` is no longer supported please use justifyContent',
    )
    const gridClasses = Object.keys(props)
        .filter(getGridPrefix)
        .map((key: KEY_OF_GRID_CLASS_MAP_TYPE) =>
            getFlexboxGridClass(
                Number.isInteger(props[key as keyof typeof props])
                    ? `${getGridPrefix(key)}-${
                          props[key as keyof typeof props]
                      }`
                    : getGridPrefix(key),
            ),
        )

    const _className = classnames([
        className,
        asCsx('textAlign', textAlign),
        asCsx('height', height),
        asCsx('width', width),
        asCsx('maxHeight', maxHeight),
        asCsx('maxWidth', maxWidth),
        asCsx('minHeight', minHeight),
        asCsx('minWidth', minWidth),
        asCsx('margin', margin),
        asCsx('marginBottom', marginBottom),
        asCsx('marginLeft', marginLeft),
        asCsx('marginRight', marginRight),
        asCsx('marginTop', marginTop),
        asCsx('padding', padding),
        asCsx('paddingBottom', paddingBottom),
        asCsx('paddingLeft', paddingLeft),
        asCsx('paddingRight', paddingRight),
        asCsx('paddingTop', paddingTop),
        asCsx('border', border),
        asCsx('borderBottom', borderBottom),
        asCsx('borderLeft', borderLeft),
        asCsx('borderRight', borderRight),
        asCsx('borderTop', borderTop),
        asCsx('position', position),
        asCsx('color', getColor(color)),
        asCsx('borderWidth', borderWidth),
        asCsx('borderRadius', borderRadius),
        asCsx('borderStyle', borderStyle),
        asCsx('borderCollapse', borderCollapse),
        asCsx('borderColor', getColor(borderColor)),
        asCsx('background', background),
        asCsx('backgroundColor', getColor(backgroundColor)),
        asCsx('textOverflow ', textOverflow),
        asCsx('whiteSpace ', whiteSpace),
        asCsx('fontSize', fontSize),
        _.isPlainObject(hover) &&
            cxs({
                ':hover': {
                    backgroundColor: getColor(hover && hover.backgroundColor),
                },
            }),
        asCsx('cursor', cursor),
        asPrefixString('display', display),
        asPrefixString('justifyContent', _justifyContent),
        asPrefixString('alignItems', _alignItems),
        asPrefixString('alignSelf', alignSelf),
        asPrefixString('alignContent', alignContent),
        asPrefixString('flexWrap', flexWrap),
        asCsx('flexGrow', flexGrow),
        asCsx('flexShrink', flexShrink),
        asCsx('flexBasis', flexBasis),
        asCsx('overflow', overflow),
        asCsx('overflowX', overflowX),
        asCsx('overflowY', overflowY),
        _.isString(direction) && styles[direction],
        flex
            ? typeof flex === 'number'
                ? styles[`flex${flex}`]
                : styles.flex1
            : false,
        ...gridClasses,
    ])

    const dataId = getGenericDataId(_className)

    return (
        <Elem
            key={eventKey}
            className={_className}
            ref={ref}
            data-id={dataId}
            {..._.omit(newProps, Object.keys(gridClassMap))}
            hidden={hidden}
        >
            {children}
        </Elem>
    )
})
